import { supabase } from '../config/supabase';
import { Club, UserClub, ClubStats } from '../types';

export const clubsService = {
  async getTotalClubCount(): Promise<number> {
    const { count, error } = await supabase
      .from('clubs')
      .select('*', { count: 'exact', head: true })
      .eq('is_active', true);
    if (error) throw error;
    return count ?? 0;
  },

  /**
   * Get all clubs
   */
  async getClubs(): Promise<Club[]> {
    const { data, error } = await supabase
      .from('clubs')
      .select('*')
      .order('name');

    if (error) throw error;
    return data || [];
  },

  /**
   * Get single club by ID
   */
  async getClubById(clubId: string): Promise<Club> {
    const { data, error } = await supabase
      .from('clubs')
      .select('*')
      .eq('id', clubId)
      .single();

    if (error) throw error;
    if (!data) throw new Error('Club not found');
    return data;
  },

  /**
   * Create new club (admin only - enforced by RLS)
   */
  async createClub(clubData: {
    name: string;
    description?: string;
    logo_url?: string;
  }): Promise<Club> {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      .from('clubs')
      .insert({
        ...clubData,
        created_by: session.user.id,
      })
      .select()
      .single();

    if (error) {
      // Handle unique constraint violation
      if (error.code === '23505') {
        throw new Error('A club with this name already exists');
      }
      throw error;
    }

    if (!data) throw new Error('Failed to create club');
    return data;
  },

  /**
   * Delete club (admin only - enforced by RLS)
   */
  async deleteClub(clubId: string): Promise<void> {
    const { error } = await supabase
      .from('clubs')
      .delete()
      .eq('id', clubId);

    if (error) throw error;
  },

  /**
   * Update club (admin only - enforced by RLS)
   */
  async updateClub(
    clubId: string,
    updates: {
      name?: string;
      description?: string | null;
      logo_url?: string | null;
      location?: string | null;
      is_verified?: boolean;
      transaction_fee_amount?: number;
    }
  ): Promise<Club> {
    // Validate fee if provided
    if (updates.transaction_fee_amount !== undefined) {
      const club = await this.getClubById(clubId);
      if (
        updates.transaction_fee_amount < club.min_transaction_fee ||
        updates.transaction_fee_amount > club.max_transaction_fee
      ) {
        throw new Error(
          `Transaction fee must be between €${club.min_transaction_fee} and €${club.max_transaction_fee}`
        );
      }
    }

    const { data, error } = await supabase
      .from('clubs')
      .update(updates)
      .eq('id', clubId)
      .select()
      .single();

    if (error) {
      // Handle unique constraint violation
      if (error.code === '23505') {
        throw new Error('A club with this name already exists');
      }
      throw error;
    }

    if (!data) throw new Error('Club not found');
    return data;
  },

  /**
   * Search clubs by name with pagination
   */
  async searchClubs(
    query: string,
    options?: { offset?: number; limit?: number }
  ): Promise<{ data: Club[]; hasMore: boolean }> {
    const offset = options?.offset || 0;
    const limit = options?.limit || 20;

    // Fetch limit + 1 to determine if there are more results
    const { data, error } = await supabase
      .from('clubs')
      .select('*')
      .ilike('name', `%${query}%`)
      .order('name')
      .range(offset, offset + limit); // Fetch one extra

    if (error) throw error;

    const clubs = data || [];
    const hasMore = clubs.length > limit;

    // Return only the requested limit
    return {
      data: hasMore ? clubs.slice(0, limit) : clubs,
      hasMore,
    };
  },

  /**
   * Get clubs with pagination (no search filter)
   */
  async getClubsPaginated(options?: {
    offset?: number;
    limit?: number;
  }): Promise<{ data: Club[]; hasMore: boolean }> {
    const offset = options?.offset || 0;
    const limit = options?.limit || 20;

    const { data, error } = await supabase
      .from('clubs')
      .select('*')
      .order('name')
      .range(offset, offset + limit);

    if (error) throw error;

    const clubs = data || [];
    const hasMore = clubs.length > limit;

    return {
      data: hasMore ? clubs.slice(0, limit) : clubs,
      hasMore,
    };
  },

  /**
   * Get user's club memberships
   */
  async getUserClubs(userId: string): Promise<UserClub[]> {
    const { data, error } = await supabase
      .from('user_clubs')
      .select('*, club:clubs(*)')
      .eq('user_id', userId)
      .order('joined_at', { ascending: false });

    if (error) throw error;
    return data || [];
  },

  /**
   * Join a club
   */
  async joinClub(clubId: string): Promise<void> {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) throw new Error('Not authenticated');

    const { error } = await supabase
      .from('user_clubs')
      .insert({
        user_id: session.user.id,
        club_id: clubId,
        is_primary: false,
      });

    if (error) {
      if (error.code === '23505') {
        throw new Error('Already a member of this club');
      }
      throw error;
    }
  },

  /**
   * Join multiple clubs at once (for onboarding)
   */
  async joinClubs(clubIds: string[]): Promise<void> {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) throw new Error('Not authenticated');
    const userId = session.user.id;

    const memberships = clubIds.map((clubId, index) => ({
      user_id: userId,
      club_id: clubId,
      is_primary: index === 0, // First club is primary
    }));

    const { error } = await supabase
      .from('user_clubs')
      .insert(memberships);

    if (error) throw error;
  },

  /**
   * Leave a club
   */
  async leaveClub(clubId: string): Promise<void> {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) throw new Error('Not authenticated');

    const { error } = await supabase
      .from('user_clubs')
      .delete()
      .eq('user_id', session.user.id)
      .eq('club_id', clubId);

    if (error) throw error;
  },

  /**
   * Set primary club for user
   */
  async setPrimaryClub(clubId: string): Promise<void> {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) throw new Error('Not authenticated');

    // First, unset all primary flags for this user. A failure here must abort:
    // continuing would set a second primary club next to the old one.
    const { error: unsetError } = await supabase
      .from('user_clubs')
      .update({ is_primary: false })
      .eq('user_id', session.user.id);

    if (unsetError) throw unsetError;

    // Then set the new primary club
    const { error } = await supabase
      .from('user_clubs')
      .update({ is_primary: true })
      .eq('user_id', session.user.id)
      .eq('club_id', clubId);

    if (error) throw error;
  },

  /**
   * Get club statistics
   */
  async getClubStats(clubId: string): Promise<ClubStats> {
    const { data, error } = await supabase
      .from('club_stats')
      .select('*')
      .eq('club_id', clubId)
      .single();

    if (error) throw error;
    if (!data) throw new Error('Club stats not found');
    return data;
  },

  /**
   * Get club leaderboard
   */
  async getClubLeaderboard(limit: number = 10): Promise<ClubStats[]> {
    const { data, error } = await supabase
      .from('club_stats')
      .select('*, club:clubs(*)')
      .order('member_count', { ascending: false })
      .limit(limit);

    if (error) throw error;
    return data || [];
  },

  /**
   * Update club transaction fee
   */
  async updateClubTransactionFee(clubId: string, amount: number): Promise<void> {
    // First, get the club's min/max boundaries
    const club = await this.getClubById(clubId);

    // Validate fee amount within boundaries
    if (amount < club.min_transaction_fee || amount > club.max_transaction_fee) {
      throw new Error(
        `Transaction fee must be between €${club.min_transaction_fee} and €${club.max_transaction_fee}`
      );
    }

    // Update the club fee
    const { error } = await supabase
      .from('clubs')
      .update({ transaction_fee_amount: amount })
      .eq('id', clubId);

    if (error) throw error;
  },

  /**
   * Get club transaction fee
   */
  async getClubTransactionFee(clubId: string): Promise<number> {
    const { data, error } = await supabase
      .from('clubs')
      .select('transaction_fee_amount')
      .eq('id', clubId)
      .single();

    if (error) throw error;
    return data?.transaction_fee_amount || 2.0;
  },
};
